import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InfoIcon, ArrowLeft, PlusCircle, X, Maximize2, Minimize2, Rocket } from 'lucide-react';

export default function PitchCreateRisks() {
  const { user } = useAuth();
  const { saveTempPitch, getTempPitch, createPitch, clearTempPitch } = useData();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newRegulatory, setNewRegulatory] = useState('');
  const [newCompliance, setNewCompliance] = useState('');
  const [newMarketRisk, setNewMarketRisk] = useState('');
  const [newExecutionRisk, setNewExecutionRisk] = useState('');
  const [newRisk, setNewRisk] = useState({ risk: '', impact: 'medium' as const, mitigation: '' });
  
  const [form, setForm] = useState(() => {
    const saved = getTempPitch();
    return {
      // Core fields
      companyName: saved?.companyName || '',
      tagline: saved?.tagline || '',
      problem: saved?.problem || '',
      solution: saved?.solution || '',
      marketSize: saved?.marketSize || '',
      businessModel: saved?.businessModel || '',
      team: saved?.team || '',
      
      // Traction
      traction: saved?.traction || {
        customerInterviews: 0,
        waitlistSignups: 0,
        lettersOfIntent: 0,
        pilotCustomers: 0,
        mvpStatus: 'none',
        mvpUrl: '',
        monthlyActiveUsers: 0,
        revenueRunRate: 0,
      },
      
      // Competition
      competitive: saved?.competitive || {
        directCompetitors: [],
        indirectCompetitors: [],
        competitiveAdvantage: '',
        barriersToEntry: [],
        substituteProducts: [],
      },
      
      // Financial
      financial: saved?.financial || {
        fundingAsk: 0,
        useOfFunds: {
          product: 0,
          marketing: 0,
          team: 0,
          operations: 0,
          legal: 0,
          other: 0,
        },
        runwayMonths: 0,
        burnRate: 0,
        revenueProjection: {
          month1: 0,
          month3: 0,
          month6: 0,
          month12: 0,
        },
        breakevenMonth: 0,
      },
      
      // GTM
      gtm: saved?.gtm || {
        customerChannels: [],
        salesStrategy: '',
        marketingPlan: '',
        partnerships: [],
        targetEarlyAdopters: '',
      },
      
      // IP
      ip: saved?.ip || {
        patentStatus: 'none',
        patentNumbers: [],
        proprietaryTechnology: '',
        technicalArchitecture: '',
        developmentStage: 'idea',
        techStack: [],
      },
      
      // Risks
      risks: saved?.risks || {
        regulatoryHurdles: [],
        complianceRequirements: [],
        keyRisks: [],
        marketRisks: [],
        executionRisks: [],
      }
    };
  });

  useEffect(() => {
    saveTempPitch(form);
  }, [form, saveTempPitch]);

  const handleBack = () => {
    navigate('/pitch/new/ip');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    // Simulate a small delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (!user) return;
    
    // Create the pitch with all data
    const id = createPitch({
      ...form,
      founderId: user.id,
    });
    
    // Clear temp data
    clearTempPitch();
    
    // Navigate to the new pitch
    navigate(`/pitch/${id}`);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullscreen(false);
      }
    }
  };

  const addArrayItem = (section: string, field: string, value: string) => {
    if (!value.trim()) return;
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: [...(prev[section as keyof typeof prev] as any)[field], value.trim()]
      }
    }));
  };

  const removeArrayItem = (section: string, field: string, index: number) => {
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: (prev[section as keyof typeof prev] as any)[field].filter((_: any, i: number) => i !== index)
      }
    }));
  };

  const addRisk = () => {
    if (!newRisk.risk.trim() || !newRisk.mitigation.trim()) return;
    setForm(prev => ({
      ...prev,
      risks: {
        ...prev.risks,
        keyRisks: [...prev.risks.keyRisks, { ...newRisk }]
      }
    }));
    setNewRisk({ risk: '', impact: 'medium', mitigation: '' });
  };

  const removeRisk = (index: number) => {
    setForm(prev => ({
      ...prev,
      risks: {
        ...prev.risks,
        keyRisks: prev.risks.keyRisks.filter((_, i) => i !== index)
      }
    }));
  };

  // Check if all required fields are filled
  const isComplete = () => {
    return (
      form.companyName &&
      form.tagline &&
      form.problem &&
      form.solution &&
      form.marketSize &&
      form.businessModel &&
      form.team
    );
  };

  return (
    <div className={`${fullscreen ? 'fixed inset-0 z-50 bg-background overflow-auto p-8' : ''}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Risks & Mitigations</h1>
            <p className="text-muted-foreground mt-1">Step 7 of 7 · Show us you've thought about what could go wrong</p>
          </div>
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Risk Assessment</CardTitle>
            <CardDescription>
              Investors respect founders who acknowledge risks and have plans to mitigate them.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <Alert className="bg-red-50 border-red-200">
              <InfoIcon className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">
                Hiding risks is a red flag. Addressing them head-on builds trust.
              </AlertDescription>
            </Alert>

            {/* Regulatory Hurdles */}
            <div className="space-y-3">
              <Label className="text-lg font-medium">Regulatory Hurdles</Label>
              <p className="text-sm text-muted-foreground mb-2">
                e.g., GDPR compliance, FDA approval, licensing requirements
              </p>
              <div className="flex gap-2">
                <Input
                  value={newRegulatory}
                  onChange={e => setNewRegulatory(e.target.value)}
                  placeholder="Add a regulatory hurdle"
                  className="text-base py-6"
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('risks', 'regulatoryHurdles', newRegulatory);
                    setNewRegulatory('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.risks.regulatoryHurdles.map((risk, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{risk}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('risks', 'regulatoryHurdles', i)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance Requirements */}
            <div className="space-y-3">
              <Label className="text-lg font-medium">Compliance Requirements</Label>
              <p className="text-sm text-muted-foreground mb-2">
                e.g., SOC2, ISO27001, HIPAA
              </p>
              <div className="flex gap-2">
                <Input
                  value={newCompliance}
                  onChange={e => setNewCompliance(e.target.value)}
                  placeholder="Add a compliance requirement"
                  className="text-base py-6"
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('risks', 'complianceRequirements', newCompliance);
                    setNewCompliance('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.risks.complianceRequirements.map((req, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{req}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('risks', 'complianceRequirements', i)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Key Risks & Mitigations */}
            <div className="space-y-4">
              <Label className="text-lg font-medium">Key Risks & Mitigations</Label>
              
              {/* Add Risk Form */}
              <div className="space-y-4 border p-6 rounded-lg bg-muted/10">
                <div className="space-y-3">
                  <Label>Risk Description</Label>
                  <Input
                    placeholder="e.g., Key engineer might leave"
                    value={newRisk.risk}
                    onChange={e => setNewRisk({ ...newRisk, risk: e.target.value })}
                    className="text-base py-6"
                  />
                </div>
                <div className="space-y-3">
                  <Label>Impact Level</Label>
                  <Select 
                    value={newRisk.impact}
                    onValueChange={(value: any) => setNewRisk({ ...newRisk, impact: value })}
                  >
                    <SelectTrigger className="text-base py-6">
                      <SelectValue placeholder="Select impact level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low Impact</SelectItem>
                      <SelectItem value="medium">Medium Impact</SelectItem>
                      <SelectItem value="high">High Impact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label>Mitigation Strategy</Label>
                  <Textarea
                    placeholder="e.g., Cross-training, equity vesting, succession plan"
                    value={newRisk.mitigation}
                    onChange={e => setNewRisk({ ...newRisk, mitigation: e.target.value })}
                    className="min-h-[100px] text-base p-4"
                  />
                </div>
                <Button onClick={addRisk} className="w-full py-6">
                  <PlusCircle className="h-5 w-5 mr-2" />
                  Add Risk
                </Button>
              </div>

              {/* Display Added Risks */}
              <div className="space-y-3 mt-4">
                {form.risks.keyRisks.map((risk, i) => (
                  <div key={i} className="flex items-start justify-between bg-secondary p-4 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{risk.risk}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          risk.impact === 'high' ? 'bg-red-100 text-red-700' :
                          risk.impact === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {risk.impact} impact
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">Mitigation: {risk.mitigation}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRisk(i)}
                      className="ml-4"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Market Risks */}
            <div className="space-y-3">
              <Label className="text-lg font-medium">Market Risks</Label>
              <div className="flex gap-2">
                <Input
                  value={newMarketRisk}
                  onChange={e => setNewMarketRisk(e.target.value)}
                  placeholder="e.g., Economic downturn, new competitor, market contraction"
                  className="text-base py-6"
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('risks', 'marketRisks', newMarketRisk);
                    setNewMarketRisk('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.risks.marketRisks.map((risk, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{risk}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('risks', 'marketRisks', i)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Execution Risks */}
            <div className="space-y-3">
              <Label className="text-lg font-medium">Execution Risks</Label>
              <div className="flex gap-2">
                <Input
                  value={newExecutionRisk}
                  onChange={e => setNewExecutionRisk(e.target.value)}
                  placeholder="e.g., Hiring challenges, technical debt, timeline delays"
                  className="text-base py-6"
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('risks', 'executionRisks', newExecutionRisk);
                    setNewExecutionRisk('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.risks.executionRisks.map((risk, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{risk}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('risks', 'executionRisks', i)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-between pt-6">
            <Button onClick={handleBack} variant="outline" size="lg" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back: IP & Technology
            </Button>
            
            <Button 
              onClick={handleSubmit} 
              size="lg" 
              className="gap-2 bg-green-600 hover:bg-green-700 px-8"
              disabled={submitting || !isComplete()}
            >
              {submitting ? (
                <>Creating Pitch...</>
              ) : (
                <>
                  Create Pitch Deck
                  <Rocket className="h-4 w-4" />
                </>
              )}
            </Button>
          </CardFooter>
        </Card>

        {!isComplete() && (
          <p className="text-center text-sm text-amber-600 mt-4">
            ⚠ Please complete all required fields in Step 1 (Basics) before creating your pitch
          </p>
        )}
      </div>
    </div>
  );
}